/**
 * Login gate (§14).
 *
 * The backend has required a session on every router since Phase 1, and the
 * production compose file turns auth on. Without this gate the deployed app
 * rendered its shell and then "Not authenticated." on every page, with no way
 * to sign in.
 *
 * With AUTH_ENABLED=false (the local default) the status call says so and this
 * renders the app straight through — local use is unchanged.
 *
 * A session that expires mid-use is handled in App.tsx: any 401 refetches the
 * auth status, which brings this screen back.
 */

import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { LogOut } from "lucide-react";
import * as React from "react";

import { ApiError, authApi, type AuthStatus } from "@/lib/api/client";
import {
  Button,
  ErrorState,
  Input,
  Panel,
  Skeleton,
} from "@/components/ui/primitives";

export const AUTH_STATUS_KEY = ["auth", "status"] as const;

export function useAuthStatus() {
  return useQuery({
    queryKey: AUTH_STATUS_KEY,
    queryFn: authApi.status,
    staleTime: Infinity,
  });
}

export function AuthGate({ children }: { children: React.ReactNode }) {
  const { data, error, isPending, refetch } = useAuthStatus();

  if (isPending) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-base px-4">
        <Skeleton className="h-40 w-full max-w-sm rounded-xl" />
      </div>
    );
  }

  if (error) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-base px-4">
        <ErrorState
          title="Can't reach MyDeck"
          message={error.message}
          onRetry={() => void refetch()}
        />
      </div>
    );
  }

  if (data.auth_enabled && !data.authenticated) return <LoginScreen />;
  return <>{children}</>;
}

function LoginScreen() {
  const queryClient = useQueryClient();
  const [password, setPassword] = React.useState("");
  const inputRef = React.useRef<HTMLInputElement>(null);

  const login = useMutation({
    mutationFn: authApi.login,
    onSuccess: (status) => {
      // Anything cached before sign-in is a 401; drop it so every page loads fresh.
      queryClient.removeQueries({ predicate: (q) => q.queryKey[0] !== "auth" });
      queryClient.setQueryData<AuthStatus>(AUTH_STATUS_KEY, status);
    },
    onError: () => {
      setPassword("");
      inputRef.current?.focus();
    },
  });

  const message = login.error ? loginErrorMessage(login.error) : null;

  return (
    <div className="relative flex min-h-screen items-center justify-center overflow-hidden px-4 py-12">
      <div className="ambient" aria-hidden />

      <div className="w-full max-w-sm">
        {/* Three blank cards, fanned and slowly breathing. Decorative only —
            nothing about the user's real cards is shown before sign-in. */}
        <div aria-hidden className="relative mx-auto mb-10 h-36 w-56 [perspective:1200px]">
          {[
            ["#2a2a30", "#4a4a52", "-14deg", "-46px", "0s"],
            ["#1d1d22", "#3a3a42", "-2deg", "0px", "0.4s"],
            ["#7a6236", "#d9bb7c", "10deg", "46px", "0.8s"],
          ].map(([from, to, rot, x, delay], i) => (
            <div
              key={i}
              className="absolute inset-x-0 top-0 mx-auto w-48"
              style={{ transform: `translateX(${x}) rotate(${rot})`, zIndex: i }}
            >
              <div
                className="card-object animate-rise"
                style={{
                  backgroundImage: `radial-gradient(130% 90% at 0% 0%, rgb(255 255 255 / 0.18), transparent 55%), linear-gradient(140deg, ${from}, ${to})`,
                  animationDelay: delay,
                }}
              >
                <div className="relative z-[3] flex h-full flex-col justify-end p-4">
                  <span className="embossed font-mono text-xs tracking-[0.2em] text-white/80">
                    •••• ••••
                  </span>
                </div>
              </div>
            </div>
          ))}
        </div>

        <div className="mb-7 animate-rise text-center" style={{ animationDelay: "0.2s" }}>
          <h1 className="text-3xl font-medium tracking-[-0.04em] text-ink">
            Every card, <em className="accent-serif">one</em> deck.
          </h1>
          <p className="mt-2 text-sm text-muted">Sign in to MyDeck.</p>
        </div>

      <Panel className="animate-rise p-6" style={{ animationDelay: "0.3s" }}>

        <form
          className="space-y-4"
          onSubmit={(e) => {
            e.preventDefault();
            if (password && !login.isPending) login.mutate(password);
          }}
        >
          {/* A visible username field would be noise for a single-user app, but
              password managers need one to save the login against. */}
          <input
            type="text"
            name="username"
            autoComplete="username"
            value="mydeck"
            readOnly
            hidden
          />
          <label className="block space-y-1.5">
            <span className="label-micro block">Password</span>
            <Input
              ref={inputRef}
              type="password"
              name="password"
              autoComplete="current-password"
              autoFocus
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              aria-invalid={message ? true : undefined}
              aria-describedby={message ? "login-error" : undefined}
            />
          </label>

          {message && (
            <p id="login-error" role="alert" className="text-xs text-overdue">
              {message}
            </p>
          )}

          <Button
            type="submit"
            variant="primary"
            className="w-full"
            disabled={!password || login.isPending}
          >
            {login.isPending ? "Signing in…" : "Sign in"}
          </Button>
        </form>
      </Panel>
      </div>
    </div>
  );
}

function loginErrorMessage(error: Error): string {
  if (error instanceof ApiError) {
    // 401 "Incorrect password.", 429 "Too many login attempts. Try again in N
    // seconds.", 503 "no password is set" — the server's wording already says
    // what happened and what to do.
    if (error.status === 401 || error.status === 429 || error.status === 503) {
      return error.message;
    }
  }
  return `Couldn't sign in: ${error.message}`;
}

/** Top-bar sign-out. Renders nothing when auth is off (local use). */
export function SignOutButton() {
  const queryClient = useQueryClient();
  const { data } = useAuthStatus();

  const logout = useMutation({
    mutationFn: authApi.logout,
    onSuccess: (status) => {
      // Gate first, so the app unmounts before its cache goes. Then drop every
      // cached response: financial data shouldn't outlive the session in
      // memory. Not `clear()` — that would detach the gate from its own status.
      queryClient.setQueryData<AuthStatus>(AUTH_STATUS_KEY, status);
      queryClient.removeQueries({ predicate: (q) => q.queryKey[0] !== "auth" });
    },
    // If the call failed the cookie may still be valid; ask rather than assume.
    onError: () => void queryClient.invalidateQueries({ queryKey: AUTH_STATUS_KEY }),
  });

  if (!data?.auth_enabled) return null;

  return (
    <Button
      variant="ghost"
      size="icon"
      onClick={() => logout.mutate()}
      disabled={logout.isPending}
      aria-label="Sign out"
      title="Sign out"
    >
      <LogOut className="h-4 w-4" />
    </Button>
  );
}
